/* =========================================================================
 * DIVISION III — the two conferences we held nothing for, and the sports
 * nobody else is chasing.
 *
 * Brian: "We must solidify d2 and d3. These are the opportunities that fly
 * under the radar that are great units."
 *
 * Two things here, and the second is the interesting one.
 *
 * 1. EMPIRE 8 AND THE SLIAC WERE THE ONLY TWO OF 65 DII/DIII CONFERENCES WE
 *    HELD NOTHING FOR. Both are now read off their own schedules.
 *
 * 2. DIVISION III SPONSORS SPORTS DIVISION I LARGELY DOES NOT — squash,
 *    equestrian, water polo, women's flag football, men's volleyball, ice
 *    hockey, gymnastics, and wrestling for BOTH genders. Every one is a real
 *    conference championship with a real winner, and none of them is on
 *    anybody's radar. That is the whole of Brian's point: a SLIAC women's
 *    wrestling ring or an ODAC equestrian ring has no competition for it.
 *
 * ⚠️ DIVISION III FOOTBALL IS OVERWHELMINGLY STANDINGS-ONLY. Eight of the ten
 * conferences read — Empire 8, NESCAC, Centennial, OAC, WIAC, ODAC, plus SLIAC
 * and UAA which do not sponsor it at all — crown football on regular-season
 * record with NO title game. Only the MIAC (a "Football Championship Week")
 * and the SCIAC (Sun/Surf division winners meet) stage one. Recording a
 * championship GAME for the other eight would be eight phantoms, and it is the
 * easiest wrong assumption available because every FBS conference has one.
 *
 * ⚠️ AND TWO CONFERENCES HAVE NO INDOOR TRACK AT ALL — the NESCAC runs a single
 * combined outdoor meet, and the SCIAC sponsors no indoor championship. An
 * indoor record for either is a phantom, and their undated outdoor records are
 * CORRECT rather than incomplete.
 * ========================================================================= */

const SRC = {
  e8: 'https://empire8.com/tournaments/',
  sliac: 'https://sliac.org/sports/2026/7/27/2627champschedule.aspx',
  nescac: 'https://nescac.com/sports/2025/3/20/2526-Champ-Dates-Sites.aspx',
  cent: 'https://centennial.org/sports/2025/8/19/2025-26-cc-championships-info.aspx',
  wiac: 'https://wiacsports.com/sports/2025/7/15/GEN_0715251411.aspx',
  miac: 'https://miacathletics.com/tournaments/',
  uaa: 'https://uaasports.info/sports/2023/9/11/uaachampionships.aspx',
  sciac: 'https://thesciac.org/sports/2023/6/30/sciac-championships.aspx',
  odac: 'https://odaconline.com/sports/2023/8/8/odac-champs-2324.aspx',
}

/**
 * ⚠️ `title` EXISTS BECAUSE THE TWO ODAC EQUESTRIAN CHAMPIONSHIPS COLLIDED.
 *
 * The key is SR-{code}-{sport}-{division}-{gender}, so two records that differ
 * only by WHEN they are held — Traditional in October, Single-Discipline in
 * March — produced one identical key and one of them would have been silently
 * lost. Caught by the catalog's collision check before anything was created.
 *
 * It is the same shape as the NCAA football rounds (three divisions collapsing
 * onto one key) and the Mountain West's individual-vs-team tennis: whenever a
 * conference crowns TWO titles in one sport, something in the name has to say
 * which. Here it also fixes the name, which would otherwise have read as two
 * identical equestrian championships.
 */
const D = (code, conference, sport, gender, date, source, note = '', title = 'Championship') => ({
  st: code, state: '', body: conference, sport, gender,
  divisions: [title],
  source,
  segment: 'Conference', level: 'Conference',
  season: '2027',
  activity: false,
  date,
  note,
})

/* ---- SLIAC — 2026-27, the only conference here whose schedule is already
 * published for the coming year. We held NOTHING for it. */
const SLIAC = [
  D('SLIAC', 'St. Louis Intercollegiate Athletic Conference', 'Golf', 'Women', '2026-10-04', SRC.sliac),
  D('SLIAC', 'St. Louis Intercollegiate Athletic Conference', 'Cross Country', 'Men', '2026-10-31', SRC.sliac),
  D('SLIAC', 'St. Louis Intercollegiate Athletic Conference', 'Cross Country', 'Women', '2026-10-31', SRC.sliac),
  D('SLIAC', 'St. Louis Intercollegiate Athletic Conference', 'Soccer', 'Men', '2026-10-31', SRC.sliac),
  D('SLIAC', 'St. Louis Intercollegiate Athletic Conference', 'Soccer', 'Women', '2026-10-31', SRC.sliac),
  D('SLIAC', 'St. Louis Intercollegiate Athletic Conference', 'Volleyball', 'Women', '2026-11-12', SRC.sliac),
  /* ⚠️ TWO WRESTLING CHAMPIONSHIPS ON CONSECUTIVE DAYS, one per gender. Most
   * conferences that sponsor wrestling crown men only — assuming that here
   * would lose a whole championship and its winner. */
  D('SLIAC', 'St. Louis Intercollegiate Athletic Conference', 'Wrestling', 'Women', '2027-02-12', SRC.sliac,
    'A separate championship from the men\'s, held the day before it.'),
  D('SLIAC', 'St. Louis Intercollegiate Athletic Conference', 'Wrestling', 'Men', '2027-02-13', SRC.sliac),
  D('SLIAC', 'St. Louis Intercollegiate Athletic Conference', 'Basketball', 'Women', '2027-02-23', SRC.sliac),
  D('SLIAC', 'St. Louis Intercollegiate Athletic Conference', 'Basketball', 'Men', '2027-02-24', SRC.sliac),
  D('SLIAC', 'St. Louis Intercollegiate Athletic Conference', 'Indoor Track & Field', '', '2027-02-26', SRC.sliac),
  D('SLIAC', 'St. Louis Intercollegiate Athletic Conference', 'Outdoor Track & Field', '', '2027-04-30', SRC.sliac),
  D('SLIAC', 'St. Louis Intercollegiate Athletic Conference', 'Golf', 'Men', '2027-05-02', SRC.sliac),
  D('SLIAC', 'St. Louis Intercollegiate Athletic Conference', 'Baseball', 'Men', '2027-05-11', SRC.sliac),
  D('SLIAC', 'St. Louis Intercollegiate Athletic Conference', 'Softball', 'Women', '2027-05-11', SRC.sliac),
]

/* ---- EMPIRE 8 — the other conference we held nothing for.
 * ⚠️ NO DATES. Its published archive runs to 2025-26 and the 2026-27 events
 * are not posted, so these carry the SPORT but not a date. That is the honest
 * shape: the championship exists and is recorded; the date is a known gap
 * rather than a guess carried over from last season, which would put a rep in
 * the wrong week. */
const E8 = [
  D('E8', 'Empire 8 Conference', 'Cross Country', 'Men', null, SRC.e8),
  D('E8', 'Empire 8 Conference', 'Cross Country', 'Women', null, SRC.e8),
  D('E8', 'Empire 8 Conference', 'Soccer', 'Men', null, SRC.e8),
  D('E8', 'Empire 8 Conference', 'Soccer', 'Women', null, SRC.e8),
  D('E8', 'Empire 8 Conference', 'Field Hockey', 'Women', null, SRC.e8),
  D('E8', 'Empire 8 Conference', 'Volleyball', 'Women', null, SRC.e8),
  D('E8', 'Empire 8 Conference', 'Wrestling', 'Men', null, SRC.e8, 'Contested as a dual-meet championship.'),
  D('E8', 'Empire 8 Conference', 'Basketball', 'Men', null, SRC.e8),
  D('E8', 'Empire 8 Conference', 'Basketball', 'Women', null, SRC.e8),
  D('E8', 'Empire 8 Conference', 'Indoor Track & Field', 'Men', null, SRC.e8),
  D('E8', 'Empire 8 Conference', 'Indoor Track & Field', 'Women', null, SRC.e8),
  /* ⚠️ WOMEN'S FLAG FOOTBALL — an NCAA emerging sport and a genuine conference
   * championship. Exactly the under-the-radar unit: a real champion, a real
   * ring order, and nobody looking for it. */
  D('E8', 'Empire 8 Conference', 'Flag Football', 'Women', null, SRC.e8,
    'An NCAA emerging sport — a real conference championship, not an exhibition.'),
  D('E8', 'Empire 8 Conference', 'Tennis', 'Men', null, SRC.e8),
  D('E8', 'Empire 8 Conference', 'Tennis', 'Women', null, SRC.e8),
  D('E8', 'Empire 8 Conference', 'Lacrosse', 'Men', null, SRC.e8),
  D('E8', 'Empire 8 Conference', 'Lacrosse', 'Women', null, SRC.e8),
  D('E8', 'Empire 8 Conference', 'Outdoor Track & Field', 'Men', null, SRC.e8),
  D('E8', 'Empire 8 Conference', 'Outdoor Track & Field', 'Women', null, SRC.e8),
  D('E8', 'Empire 8 Conference', 'Golf', 'Men', null, SRC.e8),
  D('E8', 'Empire 8 Conference', 'Golf', 'Women', null, SRC.e8),
  D('E8', 'Empire 8 Conference', 'Swimming & Diving', 'Men', null, SRC.e8),
  D('E8', 'Empire 8 Conference', 'Swimming & Diving', 'Women', null, SRC.e8),
  D('E8', 'Empire 8 Conference', 'Baseball', 'Men', null, SRC.e8),
  D('E8', 'Empire 8 Conference', 'Softball', 'Women', null, SRC.e8),
]

/* ---- THE UNDER-THE-RADAR SPORTS, across the rest of DIII ---------------- */
const UNUSUAL = [
  /* SQUASH — the NESCAC crowns it for both genders. Almost nowhere else does. */
  D('NESCAC', 'New England Small College Athletic Conference', 'Squash', 'Men', '2027-02-14', SRC.nescac),
  D('NESCAC', 'New England Small College Athletic Conference', 'Squash', 'Women', '2027-02-14', SRC.nescac),
  /* ROWING and ICE HOCKEY — also NESCAC. */
  D('NESCAC', 'New England Small College Athletic Conference', 'Rowing', '', '2027-05-10', SRC.nescac),
  D('NESCAC', 'New England Small College Athletic Conference', 'Ice Hockey', 'Men', '2027-02-28', SRC.nescac),
  D('NESCAC', 'New England Small College Athletic Conference', 'Ice Hockey', 'Women', '2027-02-28', SRC.nescac),

  /* ⚠️ EQUESTRIAN, AND THE ODAC CROWNS **TWO** SEPARATE TITLES — Traditional in
   * the autumn and Single-Discipline in the spring. Two champions, six months
   * apart. Collapsing them into one record loses a customer. */
  D('ODAC', 'Old Dominion Athletic Conference', 'Equestrian', '', '2026-10-23', SRC.odac,
    'The ODAC crowns a separate Single-Discipline champion in March.', 'Traditional Championship'),
  D('ODAC', 'Old Dominion Athletic Conference', 'Equestrian', '', '2027-03-19', SRC.odac,
    'A separate title from the Traditional championship in October.', 'Single-Discipline Championship'),
  D('ODAC', 'Old Dominion Athletic Conference', 'Volleyball', 'Men', '2027-04-06', SRC.odac),
  D('ODAC', 'Old Dominion Athletic Conference', 'Wrestling', '', '2027-02-13', SRC.odac),

  /* WATER POLO, BOTH GENDERS — the SCIAC. This is the sport whose ABSENCE in
   * the Big Ten and ACC produced four of our retired phantoms; here it is a
   * real conference championship. */
  D('SCIAC', 'Southern California Intercollegiate Athletic Conference', 'Water Polo', 'Men', '2026-11-18', SRC.sciac),
  D('SCIAC', 'Southern California Intercollegiate Athletic Conference', 'Water Polo', 'Women', '2027-04-14', SRC.sciac),

  /* ICE HOCKEY and GYMNASTICS — the WIAC. */
  D('WIAC', 'Wisconsin Intercollegiate Athletic Conference', 'Ice Hockey', 'Men', '2027-02-21', SRC.wiac),
  D('WIAC', 'Wisconsin Intercollegiate Athletic Conference', 'Ice Hockey', 'Women', '2027-02-27', SRC.wiac),
  D('WIAC', 'Wisconsin Intercollegiate Athletic Conference', 'Gymnastics', 'Women', '2027-03-07', SRC.wiac,
    'Contested as the NCGA West Regional.'),
  D('WIAC', 'Wisconsin Intercollegiate Athletic Conference', 'Wrestling', 'Men', '2027-02-14', SRC.wiac),

  /* ICE HOCKEY — the MIAC, which also stages a real football championship week,
   * one of only two DIII conferences in ten that plays for the football title. */
  D('MIAC', 'Minnesota Intercollegiate Athletic Conference', 'Ice Hockey', 'Men', '2027-02-25', SRC.miac),
  D('MIAC', 'Minnesota Intercollegiate Athletic Conference', 'Ice Hockey', 'Women', '2027-02-25', SRC.miac),

  /* WRESTLING — Centennial and the UAA. */
  D('CENT', 'Centennial Conference', 'Wrestling', '', '2027-02-14', SRC.cent),
  D('UAA', 'University Athletic Association', 'Wrestling', '', '2027-02-13', SRC.uaa),
]

export const GAPS_D3 = [...SLIAC, ...E8, ...UNUSUAL]

/* =========================================================================
 * ⚠️ WHAT DIVISION III DOES **NOT** CROWN — recorded so it is not assumed.
 * ========================================================================= */
export const D3_NOT_SPONSORED = {
  /* Football on regular-season record, NO title game. */
  standingsOnlyFootball: [
    'Empire 8 Conference', 'New England Small College Athletic Conference',
    'Centennial Conference', 'Ohio Athletic Conference',
    'Wisconsin Intercollegiate Athletic Conference', 'Old Dominion Athletic Conference',
  ],
  /* The two exceptions that DO play for it. */
  footballTitleGame: ['Minnesota Intercollegiate Athletic Conference', 'Southern California Intercollegiate Athletic Conference'],
  /* ⚠️ NO INDOOR TRACK CHAMPIONSHIP AT ALL. The NESCAC runs one combined
   * outdoor meet; the SCIAC sponsors no indoor championship. */
  noIndoorTrack: ['New England Small College Athletic Conference', 'Southern California Intercollegiate Athletic Conference'],
  /* ⚠️ THE UAA IS THE MOST STANDINGS-HEAVY CONFERENCE FOUND ANYWHERE. Its own
   * championship page carries NO basketball, soccer, baseball or softball
   * event, despite every member fielding those teams — it is an all-play-all
   * round robin and the standings decide it. A "UAA Basketball Tournament"
   * would be a phantom. */
  noTournament: {
    'University Athletic Association': ['Basketball', 'Soccer', 'Baseball', 'Softball'],
  },
}
